// src/components/SessionExpiredModal.jsx
// Окно "Сессия завершена" поверх замороженной страницы.
// reason: 'expired' — истёк срок сессии, 'replaced' — выполнен вход с другого устройства.
// Страница под окном размыта: сессии уже нет — заголовки алертов и прочие данные не должны читаться
// (например, на экране SOC на стене после входа с другого устройства).
// Вся остальная часть <body> получает атрибут inert:
// клики, Tab-фокус и экранные дикторы до неё не доходят — работать можно только с окном.
// Замораживаем именно всех детей <body>, а не только #root: модалки страницы
// (карточка алерта и т.п.) рендерятся порталом прямо в <body>.

import React, { useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { Button } from './ui';

export const SessionExpiredModal = ({ reason = 'expired', onSignOut, onSignInAgain, loading = false }) => {
  const { t } = useTranslation();
  const containerRef = useRef(null);

  useEffect(() => {
    // В полноэкранном режиме браузер показывает только развёрнутый элемент (например, «Поток
    // событий»), а окно рендерится в <body> — его бы не было видно. Выходим из fullscreen.
    if (document.fullscreenElement) document.exitFullscreen?.().catch(() => {});

    const container = containerRef.current;
    const frozen = [];
    for (const el of Array.from(document.body.children)) {
      if (el !== container && !el.inert) {
        el.inert = true;
        frozen.push(el);
      }
    }

    // inert не блокирует обработчики клавиш на document (например, Escape закрывает
    // модалку страницы) — гасим всё, что набрано не внутри нашего окна.
    const blockKeys = (e) => {
      if (!container.contains(e.target)) {
        e.stopPropagation();
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', blockKeys, true);

    // фокус на «Войти снова» (последняя кнопка окна)
    const buttons = container.querySelectorAll('button');
    buttons[buttons.length - 1]?.focus();

    return () => {
      window.removeEventListener('keydown', blockKeys, true);
      frozen.forEach(el => { el.inert = false; });
    };
  }, []);

  return createPortal(
    <div
      ref={containerRef}
      className="fixed inset-0 z-[1000] flex items-start justify-center bg-black/30 backdrop-blur-md px-4 pt-6 sm:pt-10"
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="session-expired-title"
      aria-describedby="session-expired-text"
    >
      <div className="w-full max-w-md bg-white rounded-xs shadow-2xl p-6 flex gap-4">
        <div className="w-1 rounded-full bg-brand-accent flex-shrink-0" />
        <div className="flex-1 min-w-0 space-y-4">
          <div className="space-y-1">
            <h2 id="session-expired-title" className="text-lg font-black text-gray-900">
              {t('App.session_expired_title')}
            </h2>
            <p id="session-expired-text" className="text-sm text-gray-600 leading-relaxed">
              {t(reason === 'replaced' ? 'App.session_replaced_text' : 'App.session_expired_text')}
            </p>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button variant="secondary" size="md" onClick={onSignOut} disabled={loading}>
              {t('App.sign_out')}
            </Button>
            <Button size="md" onClick={onSignInAgain} loading={loading}>
              {t('App.sign_in_again')}
            </Button>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};
